import { useQueryClient } from "@tanstack/react-query";

import {
  type CreateRuleInput,
  type RulePackageFields,
  type UpdateRuleInput,
  type UpdateRulePackageInput,
  useCreateRule,
  useCreateRulePackage,
  useDuplicateRulePackage,
  useReorderRules,
  useSetOrgRule,
  useSetOrgRulePackage,
  useSetTemplateRule,
  useSetTemplateRulePackage,
  useUpdateRule,
  useUpdateRulePackage,
} from "@api/rest/types-and-hooks";
import { notifySuccess } from "@components/notifications/notifications";
import { QA_RULE_PREVIEW_QUERY_KEY } from "@pages/QARules/constants";
import { type PackageJson, ensureOk, packageFieldsFromJson, ruleInputFromJson } from "@pages/QARules/qaRuleLabels";

const QA_API_PREFIX = "/api/basecamp/qa/";

/** `true` attaches, `false` turns off, `null` removes the row (back to the default). */
export type ToggleValue = boolean | null;

/**
 * Write calls to the basecamp QA package REST endpoints (/api/basecamp/qa/...).
 * Each throws on failure and refreshes every QA package query and any open preview.
 */
export const useQARuleApi = () => {
  const queryClient = useQueryClient();

  // Package list, single-package pages (`/api/basecamp/qa/packages/:id`) and previews.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [key] = query.queryKey;
          return typeof key === "string" && key.startsWith(QA_API_PREFIX);
        },
      }),
      queryClient.invalidateQueries({ queryKey: [QA_RULE_PREVIEW_QUERY_KEY] }),
    ]);

  const createPackage = useCreateRulePackage();
  const updatePackage = useUpdateRulePackage();
  const duplicatePackage = useDuplicateRulePackage();
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const reorderRules = useReorderRules();
  const setOrgPackage = useSetOrgRulePackage();
  const setTemplatePackage = useSetTemplateRulePackage();
  const setOrgRule = useSetOrgRule();
  const setTemplateRule = useSetTemplateRule();

  const run = async <T extends { status: number; data: unknown }>(
    request: Promise<T>,
    failure: string,
    success?: string,
  ): Promise<T> => {
    const result = ensureOk(await request, failure);
    await refresh();
    if (success) notifySuccess(success);
    return result;
  };

  /** Creates each package and its rules, then refreshes once. Stops at the first failure. */
  const importPackages = async (packages: PackageJson[]): Promise<number> => {
    let imported = 0;
    try {
      for (const json of packages) {
        const created = ensureOk(
          await createPackage.mutateAsync({ data: packageFieldsFromJson(json) }),
          `Could not import package "${json.name}".`,
        );
        if (created.status !== 201) continue;
        for (const rule of json.rules ?? []) {
          ensureOk(
            await createRule.mutateAsync({ data: { rule_set_id: created.data.id, ...ruleInputFromJson(rule) } }),
            `Could not import a rule into "${json.name}".`,
          );
        }
        if (json.is_globally_disabled) {
          ensureOk(
            await updatePackage.mutateAsync({ data: { id: created.data.id, archived: true } }),
            `Could not archive "${json.name}".`,
          );
        }
        imported += 1;
      }
    } finally {
      await refresh();
    }
    notifySuccess(`Imported ${imported} package${imported === 1 ? "" : "s"}`);
    return imported;
  };

  return {
    importPackages,
    createPackage: (fields: RulePackageFields) =>
      run(createPackage.mutateAsync({ data: fields }), "Could not create package.", "Package created"),
    updatePackage: (input: UpdateRulePackageInput) =>
      run(updatePackage.mutateAsync({ data: input }), "Could not update package.", "Package saved"),
    duplicatePackage: (id: number) =>
      run(duplicatePackage.mutateAsync({ data: { id } }), "Could not duplicate package.", "Package duplicated"),
    createRule: (input: CreateRuleInput) => run(createRule.mutateAsync({ data: input }), "Could not create rule."),
    // Rule edits save on blur, so no success toast for every field.
    updateRule: (input: UpdateRuleInput) => run(updateRule.mutateAsync({ data: input }), "Could not save rule."),
    reorderRules: (ruleSetId: number, ruleIds: number[]) =>
      run(
        reorderRules.mutateAsync({ data: { rule_set_id: ruleSetId, rule_ids: ruleIds } }),
        "Could not reorder rules.",
      ),
    setOrgPackage: (organizationId: number, ruleSetId: number, enabled: ToggleValue) =>
      run(
        setOrgPackage.mutateAsync({ data: { organization_id: organizationId, rule_set_id: ruleSetId, enabled } }),
        "Could not update the org's package.",
      ),
    setTemplatePackage: (reportTemplateId: number, ruleSetId: number, enabled: ToggleValue) =>
      run(
        setTemplatePackage.mutateAsync({
          data: { report_template_id: reportTemplateId, rule_set_id: ruleSetId, enabled },
        }),
        "Could not update the template's package.",
      ),
    setOrgRule: (organizationId: number, ruleId: number, enabled: ToggleValue) =>
      run(
        setOrgRule.mutateAsync({ data: { organization_id: organizationId, rule_id: ruleId, enabled } }),
        "Could not update the org's rule.",
      ),
    setTemplateRule: (reportTemplateId: number, ruleId: number, enabled: ToggleValue) =>
      run(
        setTemplateRule.mutateAsync({ data: { report_template_id: reportTemplateId, rule_id: ruleId, enabled } }),
        "Could not update the template's rule.",
      ),
  };
};
